import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { searchProps, useSearch } from '../../hooks/useSearch'
import { paginationProps, usePagination } from '../../hooks/usePagination'
import { Input } from '../../components/ui/adminComponents/Input'
import { LoaderPoints } from '../../components/ui/LoaderPoints'
import { Table } from '../../components/ui/adminComponents/Table'
import { AppStore } from '../../store/applicationState'
import {
  GetViewedItemsActivity,
  GetViewedItemsActivityChild,
} from '../../store/admin/statistics/viewedItemsActivity/actions'
import {
  getViewedItemsActivityChildParsed,
  getViewedItemsActivityParsed,
} from '../../utils/statisticsUtils/statisticDataHelpers'

interface Props {
  currentTenderId: number
}

const limitAmount = 400
const initialParams = { limit: limitAmount, offset: 0, search: undefined, sort: '', sortBy: '' }

const viewedItemsActivityThead = {
  stock_number: 'Stock #',
  title: 'Title',
  count_of_unique_user_views: 'Uniq views by customers',
  action: 'Action',
}

const viewedItemsActivityTheadChild = {
  email: 'Customer email',
  company: 'Company',
  count_views: 'Number of views',
}

const tableCellWidth = [120, 370, 220, 140]

export const ViewedItemsActivity: React.FC<Props> = ({ currentTenderId }) => {
  const [params, setParams] = useState<searchProps | paginationProps>(initialParams)
  const [dispatchTime, setDispatchTime] = useState(0)
  const [childIndex, setChildIndex] = useState<number[]>([])
  const dispatch = useDispatch()
  const { tenders, viewedItemsActivity } = useSelector((store: AppStore) => store)

  const didMount = useRef(false)

  usePagination(viewedItemsActivity.data, setParams, limitAmount)
  useSearch(limitAmount, params, setParams, setDispatchTime)

  useEffect(() => {
    if (didMount.current) {
      setDispatchTime((prev) => prev + 1)
      setParams(initialParams)
    }
    didMount.current = true
  }, [currentTenderId])

  useEffect(() => {
    dispatch(
      GetViewedItemsActivity.request({
        idTender: currentTenderId,
        offset: params.offset,
        limit: params.limit,
        search: params.search,
        sortBy: params.sortBy,
        sort: params.sort,
      })
    )
  }, [params.offset, params.limit, params.sortBy, params.sort, dispatchTime])

  useEffect(() => {
    if (tenders.updateTenderNow) {
      if (params.offset === 0) setDispatchTime((prev) => prev + 1)
      else setParams((prev) => ({ ...prev, offset: 0, limit: prev.limit + prev.offset }))
    }
  }, [tenders.updateTenderNow])

  /// /////////////////////////////////////////////////////////////////////////////////////////////
  const callBack = (sortBy: string) => {
    if (sortBy === 'action') return

    let value: { sortBy: string; sort: 'ASC' | 'DESC' }
    if (sortBy !== params.sortBy || params.sort === 'DESC') value = { sortBy, sort: 'ASC' }
    else value = { sortBy, sort: 'DESC' }

    setParams((prev) => ({ ...prev, offset: 0, limit: limitAmount, sortBy: value.sortBy, sort: value.sort }))
    setChildIndex([])
  }

  const getChildForViewedItemsActivity = (idItem: number) => {
    if (idItem !== -1) {
      dispatch(GetViewedItemsActivityChild.request({ idItem, idTender: currentTenderId }))
    }
  }

  const viewedItemsActivityValues = useMemo(() => {
    return viewedItemsActivity.data
      ? getViewedItemsActivityParsed(
          viewedItemsActivity.data,
          childIndex,
          getChildForViewedItemsActivity,
          currentTenderId
        )
      : null
  }, [viewedItemsActivity.data, childIndex])

  const viewedItemsActivityValuesChild = useMemo(() => {
    if (!viewedItemsActivity.childData) return null
    const result = new Map<number, object[]>()
    viewedItemsActivity.childData.forEach((value, key) => {
      result.set(key, getViewedItemsActivityChildParsed(value))
    })
    return result
  }, [viewedItemsActivity.childData?.entries()])

  useEffect(() => {
    setChildIndex(
      childIndex.filter(
        (child) =>
          viewedItemsActivity.data &&
          viewedItemsActivity.data.some((item) => +`${currentTenderId}${item.user_id}` === child)
      )
    )
  }, [viewedItemsActivity.data])

  useEffect(() => {
    if (tenders.updateTenderNow) {
      if (childIndex.length) {
        childIndex.map((child) => {
          if (viewedItemsActivity.data)
            getChildForViewedItemsActivity(child - currentTenderId * currentTenderId.toString().length)

          return null
        })
      }
    }
  }, [tenders.updateTenderNow])

  const viewedItemsActivityTable = useMemo(() => {
    return viewedItemsActivityValues ? (
      <Table
        thead={viewedItemsActivityThead}
        tbody={viewedItemsActivityValues}
        theadChild={viewedItemsActivityTheadChild}
        tbodyChild={viewedItemsActivityValuesChild}
        callBack={callBack}
        childIndex={childIndex}
        setChildIndex={setChildIndex}
        tableCellWidth={tableCellWidth}
      />
    ) : null
  }, [viewedItemsActivityValues, viewedItemsActivityValuesChild])

  return (
    <>
      <div className='flex justify-between lg:items-center mb-4 flex-col lg:flex-row'>
        <div className='w-full md:w-1/3 lg:w-1/4 mb-6 lg:mb-0'>
          <Input
            placeholder='Search...'
            value={params.search || ''}
            onChange={(e) => setParams((prev) => ({ ...prev, search: e.target.value }))}
          />
        </div>
      </div>
      <div className='relative'>
        {viewedItemsActivityTable}
        {!!viewedItemsActivityTable && viewedItemsActivity.loading && (
          <div className='absolute left-0 right-0 -bottom-6 flex justify-center'>
            <LoaderPoints className='m-auto' />
          </div>
        )}
      </div>
    </>
  )
}
